import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Container, Typography, Paper, Grid, Chip, TextField,
  Button, IconButton, Tab, Tabs, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, CircularProgress, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions, Tooltip,
  LinearProgress, Divider, InputAdornment, Select, MenuItem,
  FormControl, InputLabel, Pagination, Card
} from '@mui/material';
import {
  Dashboard as DashboardIcon, Search as SearchIcon,
  Error as ErrorIcon, Work as JobsIcon, Refresh as RefreshIcon,
  CheckCircle as OkIcon, Warning as WarnIcon, Cancel as FailIcon,
  Person as PersonIcon, Block as BlockIcon, RestoreFromTrash as RestoreIcon,
  Visibility as ViewIcon, History as AuditIcon, Storage as StorageIcon,
  Memory as MemoryIcon, Speed as SpeedIcon
} from '@mui/icons-material';
import api from '../../services/api';

const StatusDot = ({ status, small }) => {
  const color = status === 'online' || status === 'ok' ? '#4caf50'
    : status === 'warning' || status === 'degraded' ? '#ff9800'
    : status === 'error' || status === 'critical' ? '#f44336' : '#9e9e9e';
  return React.createElement('span', {
    style: {
      display: 'inline-block', width: small ? 8 : 12, height: small ? 8 : 12,
      borderRadius: '50%', backgroundColor: color, marginRight: small ? 4 : 0,
      boxShadow: '0 0 4px ' + color
    }
  });
};

const MetricCard = ({ title, value, sub, change, invertColor, icon }) => (
  <Grid item xs={6} md={3}>
    <Paper sx={{ p: 2, textAlign: 'center' }}>
      <Box sx={{ color: 'text.secondary', mb: 1 }}>{icon}</Box>
      <Typography variant="h4" fontWeight="bold">{value}</Typography>
      <Typography variant="subtitle2" color="text.secondary">{title}</Typography>
      {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
      {change !== undefined && (
        <Typography variant="caption" display="block"
          color={invertColor ? (change > 0 ? 'error.main' : 'success.main') : (change > 0 ? 'success.main' : 'error.main')}>
          {change > 0 ? '\u2191' : '\u2193'} {Math.abs(change)}%
        </Typography>
      )}
    </Paper>
  </Grid>
);

const InfoRow = ({ label, value }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    <Typography variant="body2" fontWeight="medium">{value}</Typography>
  </Box>
);

const CenteredLoader = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
);

const timeAgo = (dateStr) => {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return mins + 'm ago';
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return hrs + 'h ago';
  return Math.floor(hrs / 24) + 'd ago';
};

const SystemOverview = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchOverview = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/system-overview');
      setData(res.data); setError(null);
    } catch (err) { setError(err.response?.data?.error || 'Failed to fetch overview'); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchOverview(); }, [fetchOverview]);

  if (loading && !data) return <CenteredLoader />;
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!data) return null;

  const services = data.services || {};
  const metrics = data.metrics || {};
  const content = data.content || {};
  const database = data.database || {};
  const system = data.system || {};

  const users = metrics.users || {};
  const questionsAnswered = metrics.questionsAnswered || {};
  const posts = metrics.posts || {};
  const errors = metrics.errors || {};

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6">System Status</Typography>
        <Button startIcon={<RefreshIcon />} onClick={fetchOverview} size="small">Refresh</Button>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {Object.entries(services).map(([name, svc]) => (
          <Grid item xs={6} md={4} key={name}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <StatusDot status={svc.status} />
              <Typography variant="subtitle2" sx={{ textTransform: 'capitalize', mt: 1 }}>{name}</Typography>
              <Typography variant="caption" color="text.secondary">
                {svc.responseMs ? svc.responseMs + 'ms' : svc.status}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <MetricCard title="Users" value={(users.total || 0).toLocaleString()} sub={'+' + (users.last24h || 0) + ' today'} change={users.change} icon={<PersonIcon />} />
        <MetricCard title="Questions Answered" value={(questionsAnswered.total || 0).toLocaleString()} sub={'+' + (questionsAnswered.last24h || 0) + ' today'} change={questionsAnswered.change} icon={<SpeedIcon />} />
        <MetricCard title="Posts" value={(posts.total || 0).toLocaleString()} sub={'+' + (posts.last24h || 0) + ' today'} change={posts.change} icon={<DashboardIcon />} />
        <MetricCard title="Errors (24h)" value={errors.last24h || 0} change={errors.change} invertColor icon={<ErrorIcon />} />
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom><StorageIcon sx={{ mr: 1, verticalAlign: 'middle' }} />Content</Typography>
            <InfoRow label="Questions" value={(content.questions || 0).toLocaleString()} />
            <InfoRow label="Worked Examples" value={(content.workedExamples || 0).toLocaleString()} />
            <InfoRow label="DB Size" value={(database.sizeGB || '0') + ' GB'} />
            <InfoRow label="Collections" value={database.collections || 0} />
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom><MemoryIcon sx={{ mr: 1, verticalAlign: 'middle' }} />System</Typography>
            <InfoRow label="Node.js" value={system.nodeVersion || 'N/A'} />
            <InfoRow label="Uptime" value={data.uptimeFormatted || 'N/A'} />
            <InfoRow label="Heap Used" value={(system.heapUsedMB || 0) + ' MB'} />
            <InfoRow label="System Memory" value={(system.systemMemoryUsedPercent || 0) + '%'} />
            <LinearProgress variant="determinate" value={system.systemMemoryUsedPercent || 0}
              color={(system.systemMemoryUsedPercent || 0) > 85 ? 'error' : 'primary'} sx={{ mt: 1 }} />
          </Paper>
        </Grid>
      </Grid>

      {content.gradeDistribution && content.gradeDistribution.length > 0 && (
        <Paper sx={{ p: 2, mt: 2 }}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Users by Grade</Typography>
          <Grid container spacing={1}>
            {content.gradeDistribution.map(g => (
              <Grid item xs={4} md={2} key={g.grade}>
                <Card variant="outlined" sx={{ textAlign: 'center', p: 1 }}>
                  <Typography variant="h5" fontWeight="bold">{g.users}</Typography>
                  <Typography variant="caption">Grade {g.grade}</Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}
    </Box>
  );
};

const UserLookup = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetail, setUserDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState(null);
  const [actionDialog, setActionDialog] = useState(null);
  const [actionReason, setActionReason] = useState('');

  const searchUsers = async (e) => {
    if (e) e.preventDefault();
    if (query.trim().length < 2) return;
    setLoading(true); setError(null);
    try {
      const res = await api.get('/admin/users/search?q=' + encodeURIComponent(query));
      setResults(res.data);
    } catch (err) { setError(err.response?.data?.error || 'Search failed'); }
    setLoading(false);
  };

  const viewUserDetail = async (userId) => {
    setDetailLoading(true);
    try {
      const res = await api.get('/admin/users/' + userId);
      setUserDetail(res.data); setSelectedUser(userId);
    } catch (err) { setError(err.response?.data?.error || 'Failed to load details'); }
    setDetailLoading(false);
  };

  const handleUserAction = async (action) => {
    try {
      await api.post('/admin/users/' + selectedUser + '/' + action, { reason: actionReason });
      setActionDialog(null); setActionReason('');
      viewUserDetail(selectedUser);
    } catch (err) { setError(err.response?.data?.error || 'Failed to ' + action + ' user'); }
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') searchUsers();
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <TextField fullWidth placeholder="Search by username, email, or user ID..."
          value={query} onChange={e => setQuery(e.target.value)} onKeyDown={handleSearchKeyDown}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>
          }} />
        <Button variant="contained" onClick={searchUsers} disabled={loading}>Search</Button>
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
        User lookup is logged for audit purposes.
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Grid container spacing={2}>
        <Grid item xs={12} md={selectedUser ? 5 : 12}>
          {loading && <CenteredLoader />}
          {results && (
            <>
              <Typography variant="subtitle2" gutterBottom>{results.count} result{results.count !== 1 ? 's' : ''}</Typography>
              <TableContainer component={Paper} sx={{ maxHeight: 500 }}>
                <Table size="small" stickyHeader>
                  <TableHead><TableRow>
                    <TableCell>Username</TableCell><TableCell>Email</TableCell>
                    <TableCell>Grade</TableCell><TableCell>Status</TableCell><TableCell></TableCell>
                  </TableRow></TableHead>
                  <TableBody>
                    {(results.users || []).map(u => (
                      <TableRow key={u._id} hover selected={selectedUser === u._id}
                        sx={{ cursor: 'pointer' }} onClick={() => viewUserDetail(u._id)}>
                        <TableCell>@{u.username}</TableCell>
                        <TableCell>{u.email}</TableCell>
                        <TableCell>{u.profile?.grade || '-'}</TableCell>
                        <TableCell><StatusDot status={u.isActive !== false ? 'online' : 'error'} small /></TableCell>
                        <TableCell><IconButton size="small"><ViewIcon fontSize="small" /></IconButton></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </Grid>

        {selectedUser && (
          <Grid item xs={12} md={7}>
            {detailLoading ? <CenteredLoader /> : userDetail && (
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  @{userDetail.user.username}
                  <Chip label={userDetail.user.isActive !== false ? 'Active' : 'Deactivated'}
                    color={userDetail.user.isActive !== false ? 'success' : 'error'} size="small" sx={{ ml: 1 }} />
                  {userDetail.user.role === 'admin' && <Chip label="Admin" color="primary" size="small" sx={{ ml: 1 }} />}
                </Typography>
                <InfoRow label="Email" value={userDetail.user.email} />
                <InfoRow label="Grade" value={userDetail.user.profile?.grade || 'Not set'} />
                <InfoRow label="Registered" value={new Date(userDetail.user.createdAt).toLocaleDateString()} />
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Activity</Typography>
                <InfoRow label="Questions Answered" value={(userDetail.activity?.questionsAnswered || 0) + ' (' + (userDetail.activity?.correctRate || 0) + '% correct)'} />
                <InfoRow label="Posts Created" value={userDetail.activity?.postsCreated || 0} />
                {userDetail.activity?.topicBreakdown && userDetail.activity.topicBreakdown.length > 0 && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Topics</Typography>
                    {userDetail.activity.topicBreakdown.map(t => (
                      <Box key={t.topic} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2">{t.topic}</Typography>
                        <Typography variant="body2">{t.correct}/{t.total} ({t.rate}%)</Typography>
                      </Box>
                    ))}
                  </>
                )}
                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {userDetail.user.isActive !== false ? (
                    <Button variant="outlined" color="error" size="small" startIcon={<BlockIcon />}
                      onClick={() => setActionDialog('deactivate')}>Deactivate</Button>
                  ) : (
                    <Button variant="outlined" color="success" size="small" startIcon={<RestoreIcon />}
                      onClick={() => setActionDialog('reactivate')}>Reactivate</Button>
                  )}
                </Box>
              </Paper>
            )}
          </Grid>
        )}
      </Grid>

      <Dialog open={!!actionDialog} onClose={() => setActionDialog(null)}>
        <DialogTitle>{actionDialog === 'deactivate' ? 'Deactivate User' : 'Reactivate User'}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {actionDialog === 'deactivate' ? 'This will prevent the user from logging in.' : 'This will restore access.'}
          </Typography>
          {actionDialog === 'deactivate' && (
            <TextField fullWidth label="Reason (optional)" value={actionReason}
              onChange={e => setActionReason(e.target.value)} multiline rows={2} />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActionDialog(null)}>Cancel</Button>
          <Button variant="contained" color={actionDialog === 'deactivate' ? 'error' : 'success'}
            onClick={() => handleUserAction(actionDialog)}>Confirm</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const ErrorFeed = () => {
  const [errors, setErrors] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({ hours: '24', level: '', status: 'open' });

  const fetchErrors = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: page, limit: 20 });
      Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
      const res = await api.get('/admin/errors?' + params.toString());
      setErrors(res.data.errors || []);
      setStats(res.data.stats || null);
      if (res.data.pagination) {
        setTotalPages(res.data.pagination.pages || 1);
      }
    } catch (err) { console.error('Error fetch failed:', err); }
    setLoading(false);
  }, [filters, page]);

  useEffect(() => { fetchErrors(); }, [fetchErrors]);

  const handleAction = async (errorId, action) => {
    try {
      await api.post('/admin/errors/' + errorId + '/action', { action: action }); fetchErrors();
    } catch (err) { console.error('Action failed:', err); }
  };

  const levelColor = { critical: 'error', error: 'error', warn: 'warning', info: 'info' };

  return (
    <Box>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={4} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Time</InputLabel>
              <Select value={filters.hours} label="Time"
                onChange={e => setFilters(prev => ({ ...prev, hours: e.target.value }))}>
                <MenuItem value="1">Last Hour</MenuItem>
                <MenuItem value="6">6 Hours</MenuItem>
                <MenuItem value="24">24 Hours</MenuItem>
                <MenuItem value="168">Week</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={4} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Level</InputLabel>
              <Select value={filters.level} label="Level"
                onChange={e => setFilters(prev => ({ ...prev, level: e.target.value }))}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="critical">Critical</MenuItem>
                <MenuItem value="error">Error</MenuItem>
                <MenuItem value="warn">Warning</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={4} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select value={filters.status} label="Status"
                onChange={e => setFilters(prev => ({ ...prev, status: e.target.value }))}>
                <MenuItem value="open">Open</MenuItem>
                <MenuItem value="acknowledged">Acknowledged</MenuItem>
                <MenuItem value="resolved">Resolved</MenuItem>
                <MenuItem value="all">All</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={4} md={2}>
            <Button fullWidth startIcon={<RefreshIcon />} onClick={fetchErrors} variant="outlined">Refresh</Button>
          </Grid>
        </Grid>
      </Paper>

      {stats && (
        <Grid container spacing={1} sx={{ mb: 2 }}>
          <Grid item xs={3}><Paper sx={{ p: 1, textAlign: 'center' }}>
            <Typography variant="h5" fontWeight="bold">{stats.total || 0}</Typography>
            <Typography variant="caption">Total</Typography>
          </Paper></Grid>
          {Object.entries(stats.byLevel || {}).map(([level, count]) => (
            <Grid item xs={3} key={level}><Paper sx={{ p: 1, textAlign: 'center' }}>
              <Typography variant="h5" fontWeight="bold" color={(levelColor[level] || 'inherit') + '.main'}>{count}</Typography>
              <Typography variant="caption" sx={{ textTransform: 'capitalize' }}>{level}</Typography>
            </Paper></Grid>
          ))}
        </Grid>
      )}

      {loading ? <CenteredLoader /> : errors.length === 0 ? (
        <Alert severity="success" sx={{ mt: 2 }}>No errors found.</Alert>
      ) : errors.map(err => (
        <Paper key={err._id} sx={{ p: 2, mb: 1, borderLeft: 3, borderColor: (levelColor[err.level] || 'grey') + '.main' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box>
              <Box sx={{ display: 'flex', gap: 1, mb: 0.5 }}>
                <Chip label={(err.level || 'unknown').toUpperCase()} color={levelColor[err.level] || 'default'} size="small" />
                <Chip label={err.category || 'unknown'} variant="outlined" size="small" />
                {err.occurrenceCount > 1 && <Chip label={'\u00d7' + err.occurrenceCount} size="small" color="secondary" />}
              </Box>
              <Typography variant="body1">{err.humanMessage || err.message}</Typography>
              {err.source?.route && <Typography variant="caption" color="text.secondary">{err.source.method} {err.source.route}</Typography>}
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="caption" color="text.secondary">{timeAgo(err.lastOccurrence || err.createdAt)}</Typography>
              <Box sx={{ mt: 1, display: 'flex', gap: 0.5 }}>
                <Tooltip title="Resolve"><IconButton size="small" color="success" onClick={() => handleAction(err._id, 'resolve')}><OkIcon fontSize="small" /></IconButton></Tooltip>
                <Tooltip title="Ignore"><IconButton size="small" onClick={() => handleAction(err._id, 'ignore')}><BlockIcon fontSize="small" /></IconButton></Tooltip>
              </Box>
            </Box>
          </Box>
        </Paper>
      ))}

      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Pagination count={totalPages} page={page}
            onChange={(_, p) => setPage(p)} />
        </Box>
      )}
    </Box>
  );
};

const BackgroundJobs = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    try { const res = await api.get('/admin/jobs'); setJobs(res.data.jobs || []); }
    catch (err) { console.error('Failed to fetch jobs:', err); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchJobs(); }, [fetchJobs]);

  if (loading) return <CenteredLoader />;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6">Background Jobs</Typography>
        <Button startIcon={<RefreshIcon />} onClick={fetchJobs} size="small">Refresh</Button>
      </Box>
      {jobs.map((job, i) => (
        <Paper key={i} sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {job.status === 'ok' ? <OkIcon color="success" /> : job.status === 'error' ? <FailIcon color="error" /> : <WarnIcon color="warning" />}
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" fontWeight="bold">{job.name}</Typography>
              {job.description && <Typography variant="body2" color="text.secondary">{job.description}</Typography>}
              {job.error && <Typography variant="body2" color="error.main">{job.error}</Typography>}
              {job.lastRun && <Typography variant="caption" color="text.secondary">Last: {new Date(job.lastRun).toLocaleString()}</Typography>}
              {job.nextRun && <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>Next: {job.nextRun}</Typography>}
              {job.connections && <Typography variant="caption" color="text.secondary" display="block">
                Connections - Current: {job.connections.current}, Available: {job.connections.available}
              </Typography>}
            </Box>
            <Chip label={(job.status || 'unknown').toUpperCase()} size="small"
              color={job.status === 'ok' ? 'success' : job.status === 'error' ? 'error' : 'warning'} />
          </Box>
        </Paper>
      ))}
    </Box>
  );
};

const AuditLog = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/audit-log?page=' + page + '&limit=30');
      setLogs(res.data.logs || []);
      if (res.data.pagination) {
        setTotalPages(res.data.pagination.pages || 1);
      }
    } catch (err) { console.error('Failed to fetch audit log:', err); }
    setLoading(false);
  }, [page]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);
  if (loading) return <CenteredLoader />;

  return (
    <Box>
      <Typography variant="h6" gutterBottom>Admin Audit Log</Typography>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead><TableRow>
            <TableCell>Time</TableCell><TableCell>Admin</TableCell>
            <TableCell>Action</TableCell><TableCell>Target</TableCell><TableCell>Status</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {logs.map(log => (
              <TableRow key={log._id}>
                <TableCell><Typography variant="caption">{new Date(log.createdAt).toLocaleString()}</Typography></TableCell>
                <TableCell>{log.adminUsername}</TableCell>
                <TableCell><Chip label={(log.action || '').replace(/_/g, ' ')} size="small" variant="outlined" /></TableCell>
                <TableCell><Typography variant="caption">{log.target?.description || '-'}</Typography></TableCell>
                <TableCell><StatusDot status={log.success ? 'online' : 'error'} small /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Pagination count={totalPages} page={page}
            onChange={(_, p) => setPage(p)} />
        </Box>
      )}
    </Box>
  );
};

const AdminDashboard = () => {
  const [tab, setTab] = useState(0);

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight="bold">MathZA Admin Panel</Typography>
        <Chip label="Admin" color="primary" size="small" />
      </Box>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
        <Tab icon={<DashboardIcon />} label="Overview" iconPosition="start" />
        <Tab icon={<PersonIcon />} label="Users" iconPosition="start" />
        <Tab icon={<ErrorIcon />} label="Errors" iconPosition="start" />
        <Tab icon={<JobsIcon />} label="Jobs" iconPosition="start" />
        <Tab icon={<AuditIcon />} label="Audit" iconPosition="start" />
      </Tabs>
      {tab === 0 && <SystemOverview />}
      {tab === 1 && <UserLookup />}
      {tab === 2 && <ErrorFeed />}
      {tab === 3 && <BackgroundJobs />}
      {tab === 4 && <AuditLog />}
    </Container>
  );
};

export default AdminDashboard;
